import { createTrackingConfig } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import CustomComplaintSplitView from './CustomComplaintSplitView';
import ComplaintActionsIntegration from './components/ComplaintActionsIntegration';
import ComplaintActionTakenIntegration from './components/ComplaintActionTakenIntegration';
import ComplaintProductIntegration from './components/ComplaintProductIntegration';
import PossibleCausesAccordion from './components/PossibleCausesAccordion';
import ComplaintReferenceDocument from './components/ComplaintReferenceDocument';

export const complaintConfig = {
  entityName: 'Complaint',
  
  // Entity information for file attachments
  entityTypeId: 7, // Complaint entity type ID (different from Documents which is 1, CAPA which is 3)
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/complaint',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      duplicate: (id) => `/${id}/duplicate`,
      bulkDelete: '/bulk-delete',
      // File attachment endpoints - removed complaint-specific endpoints
      // Files are included in the complaint response and can be fetched via generic /file-attachment/entity/7/{id}
      // fileAttachments: (id) => `/${id}/file-attachments`, // This endpoint doesn't exist in complaint controller
      // fileAttachmentCount: (id) => `/${id}/file-attachment-count`, // Not needed - included in response
      // withFileAttachments: (id) => `/${id}/with-file-attachments` // Not needed - included in response
    },
    lookupEndpoints: {
      departments: '/department',
      users: '/users',
      complaintTypes: '/complaint-type',
      complaintStatuses: '/complaint-status',
      complaintActionTypes: '/complaint-action-type',
      complaintActionTakenTypes: '/complaint-action-taken-type',
      priorities: '/priority',
      customers: '/customer',
      distributors: '/distributor',
      complaintSources: '/complaint-source',
      complaintCategoryNonMedical: '/complaint-category-non-medical',
      complaintSampleAvailable: '/complaint-sample-available',
      complaintSampleInspection: '/complaint-sample-inspection',
      complaintForwardTo: '/complaint-forward-to',
      complaintFindingType: '/complaint-finding-type',
      complaintInvestigationCategory: '/complaint-investigation-category',
      riskAssessments: '/risk-assessment',
      deviations: '/deviation'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/complaint',
      withFileAttachments: '/complaint/with-file-attachments',
      fileAttachments: (entityTypeId) => `/file-attachment/entity/${entityTypeId}/*`
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '50%', // 30% of table width
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
    tableVariant: 'default',  // 'default' | 'modern' | 'cards' | 'board' | 'timeline' | 'advancedCard'
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
                            // advancedCard: advanced card table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth, // From app config: '60%'
    // Bump when default column order changes so stale localStorage order is ignored
    tableStorageKey: 'complaint-v3',

    // Card positioning configuration
    rowPositioning: 'auto', // 'auto', 'fixed', or 'custom'
    customRowOrder: [], // Array of item IDs for custom row ordering
    columnPositioning: 'auto', // 'auto', 'fixed', or 'custom'

    // Action button visibility
    showViewButton: true,
    showEditButton: true,
    showDeleteButton: true,
    showDuplicateButton: true // Show duplicate button in table actions
  },

  // Custom Split View Panel configuration for Complaint module
  customSplitViewPanel: {
    enabled: true,
    component: CustomComplaintSplitView  // Use the custom Complaint split view component
  },

  // Features configuration
  features: {
    fileAttachments: true, // Enable file attachment functionality
    masterEntityTypeId: 7, // Complaint master entity type ID for audit logs
    approvalRequests: true, // Enable approval request functionality
  },

  // Review and Approval configuration
  approval: {
    enabled: true, // Master switch for review and approval functionality
    showInForm: true, // Show review and approval request button in complaint form
    showInDetails: true, // Show review and approval integration in complaint details tab
    requireApprovalForClose: false, // Require review and approval before closing complaint
    allowMultipleApprovals: true, // Allow multiple review and approval requests per complaint
    defaultApprovalType: 'single', // Default approval type when creating requests
    approvalButtonText: 'Request Review and Approval', // Text for review and approval request button
    approvalButtonIcon: 'CheckCircleIcon', // Icon component name (from Icons.js) for approval button icon
    showApprovalStatus: true, // Show current review and approval status in complaint view
    showApprovalActions: true, // Show approval action buttons (approve, reject, etc.)
    showApprovalHistory: true, // Show review and approval history in complaint timeline
    formMode: 'modal', // Use modal popup for review and approval form
  },
  
  // Custom handlers for complaint-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in complaintPage
    onDelete: null, // Will be defined in complaintPage
    onLoad: null, // Will be defined in complaintPage
    onFormOpen: null, // Will be defined in complaintPage
  },
  
  // Service configuration
  service: {
    name: 'complaintService',
    methods: {
      getAll: 'getAllComplaints',
      getById: 'getComplaintById', 
      create: 'createComplaint',
      update: 'updateComplaint',
      delete: 'deleteComplaint',
      validate: 'validateData'
    }
  },
  
  // Display Configuration
  displayName: 'Complaint Management',
  description: 'Customer and internal complaint management system',
  icon: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
  
  // Fields to remove from forms (relations, computed, system fields, and fields handled by custom components)
  // Note: Possible Causes fields (humanFactor, equipment, methods, materials, supplier and their notes)
  // are NOT removed here because they need to be submitted to the backend, even though they're
  // rendered by the PossibleCausesAccordion custom component
  fieldsToRemove: [
    'id', 'complaintType', 'priority', 'source', 'customer', 'distributor',
    'sampleAvailable', 'complaintCategoryNonMedical', 'investigationCategory', 
    'findingType', 'recordedBy', 'department', 'status', 'riskAssessment', 
    'deviation', 'actions', 'attachments', 'timelineEntries', 'createdAt', 'updatedAt',
    'user', 'complaintTimelineEntries', 'complaintActions', 'complaintActionTaken', 'closedBy', 'linkedCAPA',
    'fileAttachments', 'fileAttachmentCount', 'sampleInspection', 'forwardTo', 'involvedDepartments',
    'createdBy', 'updatedBy', 'createdById', 'updatedById',
    '_count',
  ],

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  // Fields are ordered to match formSections order
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    // Basic Information Section
    {
      name: 'code',
      label: 'Complaint Code',
      type: 'text',
      required: true,
      placeholder: 'Enter complaint code',
      defaultValue: '',
      table: { header: 'Code', width: '130px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 50,
        message: 'Complaint code is required and must not exceed 50 characters'
      }
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Type to search existing titles...',
      //autocompleteEndpoint: '/complaint/titles',
      table: { 
        header: 'Title', 
        width: '200px', 
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
        maxLength: 50
      },
      filterable: true,
      validation: {
        required: true,
        maxLength: 200,
        message: 'Title is required and must not exceed 200 characters'
      }
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter detailed complaint description',
      required: true,
      validation: {
        required: true,
        maxLength: 2000,
        message: 'Description is required and must not exceed 2000 characters'
      },
      table: { 
        header: 'Description', 
        width: '300px', 
        sortable: true,
        cellRenderer: 'lineClamp',
        maxLines: 3 // Show up to 3 lines, then truncate
      },
      filterable: false,
      rows: 5
    },
    {
      name: 'complaintTypeId',
      label: 'Complaint Type',
      type: 'select',
      required: false,
      lookupEndpoint: 'complaintTypes',
      options: [],
      disabledInEdit: true, // Disable in edit mode
      validation: {
        integer: true,
        message: 'Please select a valid complaint type'
      },
      table: {
        header: 'Type',
        width: '150px',
        sortable: true,
        displayField: 'complaintType',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'priorityId',
      label: 'Priority',
      type: 'select',
      required: false,
      lookupEndpoint: 'priorities',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid priority'
      },
      table: {
        header: 'Priority',
        width: '120px',
        sortable: true,
        displayField: 'priority',
        cellRenderer: 'priorityBadge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: true,
      lookupEndpoint: 'complaintStatuses',
      options: [],
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid status'
      },
      table: {
        header: 'Status',
        width: '120px',
        sortable: true,
        displayField: 'status',
        cellRenderer: 'statusBadge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Source / Customer Information Section
    {
      name: 'sourceId',
      label: 'Recipient Source',
      type: 'select',
      required: false,
      lookupEndpoint: 'complaintSources',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid recipient source'
      },
      table: {
        header: 'Recipient Source',
        width: '120px',
        sortable: true,
        displayField: 'source',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'sourceDetails',
      label: 'Recipient Source Details',
      type: 'text',
      placeholder: 'Enter additional recipient source details',
      required: false,
      validation: {
        maxLength: 200,
        message: 'Source details must not exceed 200 characters'
      },
      table: false,
      filterable: false
    },
    {
      name: 'customerId',
      label: 'Depositor',
      type: 'select',
      required: false,
      lookupEndpoint: 'customers',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid depositor'
      },
      table: {
        header: 'Depositor',
        width: '150px',
        sortable: true,
        displayField: 'customer',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'distributorId',
      label: 'Distributor',
      type: 'select',
      required: false,
      lookupEndpoint: 'distributors',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid distributor'
      },
      table: {
        header: 'Distributor',
        width: '150px',
        sortable: true,
        displayField: 'distributor',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'customerReportedBy',
      label: 'Reported By (Depositor)',
      type: 'text',
      placeholder: 'Enter name of person who reported the complaint on behalf of the depositor',
      required: false,
      validation: {
        maxLength: 200,
        message: 'Reported by name on behalf of the depositor must not exceed 200 characters'
      },
      table: { 
        header: 'Reported By (Depositor)', 
        width: '150px', 
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
        maxLength: 50
      },
      filterable: true,
      filterType: 'text'
    },
    {
      name: 'sampleAvailableId',
      label: 'Sample Available',
      type: 'select',
      required: false,
      lookupEndpoint: 'complaintSampleAvailable',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid option'
      },
      table: {
        header: 'Sample',
        width: '100px',
        sortable: true,
        displayField: 'sampleAvailable',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'sampleInspectionId',
      label: 'Inspection of complaint sample',
      type: 'select',
      required: false,
      lookupEndpoint: 'complaintSampleInspection',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid option'
      },
      table: {
        header: 'Sample Inspection',
        width: '150px',
        sortable: true,
        displayField: 'sampleInspection',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'forwardToIds',
      label: 'Forward To',
      type: 'multiSelect',
      required: false,
      lookupEndpoint: 'complaintForwardTo',
      options: [],
      validation: {
        array: true,
        message: 'Please select at least one option'
      },
      table: {
        header: 'Forward To',
        width: '200px',
        sortable: false,
        displayField: 'forwardTo',
        cellRenderer: 'array'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'reportedAt',
      label: 'Reported Date',
      type: 'date',
      required: false,
      defaultValue: new Date().toISOString().split('T')[0],
      table: { 
        header: 'Reported Date', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid reported date'
      }
    },
    // Product Information - Now handled by ComplaintProductIntegration component
    // Products are stored in a separate ComplaintProduct model (one-to-many relationship)
    // Complaint Category Section
    {
      name: 'complaintCategoryNonMedicalId',
      label: 'Complaint Category (Non-Medical)',
      type: 'select',
      required: false,
      lookupEndpoint: 'complaintCategoryNonMedical',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid category'
      },
      table: {
        header: 'Category',
        width: '150px',
        sortable: true,
        displayField: 'complaintCategoryNonMedical',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'complaintCategoryNonMedicalOther',
      label: 'Other Category Details',
      type: 'text',
      placeholder: 'Specify other category details',
      required: false,
      validation: {
        maxLength: 200,
        message: 'Category details must not exceed 200 characters'
      },
      table: false,
      filterable: false
    },
    {
      name: 'complaintCategoryMedical',
      label: 'Complaint Category (Medical)',
      type: 'text',
      placeholder: 'Enter medical category',
      required: false,
      validation: {
        maxLength: 200,
        message: 'Medical category must not exceed 200 characters'
      },
      table: false,
      filterable: false
    },
    {
      name: 'recordedById',
      label: 'Recorded By',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      options: [],
      table: {
        header: 'Recorded By',
        width: '150px',
        sortable: true,
        displayField: 'recordedBy',
        cellRenderer: 'userDisplay'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
    },
    {
      name: 'recordedAt',
      label: 'Recorded Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Recorded Date', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid recorded date'
      }
    },
    // Investigation Section
    {
      name: 'investigationStartDate',
      label: 'Investigation Start Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Investigation Start', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid investigation start date'
      }
    },
    {
      name: 'investigationCategoryId',
      label: 'Investigation Category',
      type: 'select',
      required: false,
      lookupEndpoint: 'complaintInvestigationCategory',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid investigation category'
      },
      table: {
        header: 'Investigation',
        width: '150px',
        sortable: true,
        displayField: 'investigationCategory',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Conclusion Section
    {
      name: 'findingTypeId',
      label: 'Finding Type',
      type: 'select',
      required: false,
      lookupEndpoint: 'complaintFindingType',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid finding type'
      },
      table: {
        header: 'Finding',
        width: '120px',
        sortable: true,
        displayField: 'findingType',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'involvedDepartmentsIds',
      label: 'Involved Departments',
      type: 'multiSelect',
      required: false,
      lookupEndpoint: 'departments',
      options: [],
      validation: {
        message: 'Please select valid departments'
      },
      table: {
        header: 'Involved Departments',
        width: '250px',
        sortable: false,
        displayField: 'involvedDepartments',
        cellRenderer: 'array'
      },
      filterable: false,
      placeholder: 'Select departments involved in this complaint'
    },
    // Possible Causes Section
    {
      name: 'humanFactor',
      label: 'Human Factor',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'humanFactorNote',
      label: 'Human Factor Note',
      type: 'textarea',
      placeholder: 'Enter human factor details',
      required: false,
      validation: {
        maxLength: 1000,
        message: 'Human factor note must not exceed 1000 characters'
      },
      table: false,
      filterable: false,
      rows: 2
    },
    {
      name: 'equipment',
      label: 'Equipment',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'equipmentNote',
      label: 'Equipment Note',
      type: 'textarea',
      placeholder: 'Enter equipment details',
      required: false,
      validation: {
        maxLength: 1000,
        message: 'Equipment note must not exceed 1000 characters'
      },
      table: false,
      filterable: false,
      rows: 2
    },
    {
      name: 'methods',
      label: 'Methods',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'methodsNote',
      label: 'Methods Note',
      type: 'textarea',
      placeholder: 'Enter methods details',
      required: false,
      validation: {
        maxLength: 1000,
        message: 'Methods note must not exceed 1000 characters'
      },
      table: false,
      filterable: false,
      rows: 2
    },
    {
      name: 'materials',
      label: 'Materials',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'materialsNote',
      label: 'Materials Note',
      type: 'textarea',
      placeholder: 'Enter materials details',
      required: false,
      validation: {
        maxLength: 1000,
        message: 'Materials note must not exceed 1000 characters'
      },
      table: false,
      filterable: false,
      rows: 2
    },
    {
      name: 'supplier',
      label: 'Supplier',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'supplierNote',
      label: 'Supplier Note',
      type: 'textarea',
      placeholder: 'Enter supplier details',
      required: false,
      validation: {
        maxLength: 1000,
        message: 'Supplier note must not exceed 1000 characters'
      },
      table: false,
      filterable: false,
      rows: 2
    },
    {
      name: 'completionComments',
      label: 'Completion Comments',
      type: 'textarea',
      placeholder: 'Enter completion observations/comments',
      required: false,
      validation: {
        maxLength: 2000,
        message: 'Completion comments must not exceed 2000 characters'
      },
      table: false,
      filterable: false,
      rows: 6
    },
    // Links Section
    {
      name: 'riskAssessmentId',
      label: 'Risk Assessment',
      type: 'select',
      required: false,
      lookupEndpoint: 'riskAssessments',
      labelField: 'code', // Display code instead of title in the select dropdown
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid risk assessment'
      },
      table: {
        header: 'Risk Assessment',
        width: '150px',
        sortable: true,
        displayField: 'riskAssessment',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'deviationId',
      label: 'Deviation',
      type: 'select',
      required: false,
      lookupEndpoint: 'deviations',
      labelField: 'code', // Display Deviation Code instead of Title in the select dropdown
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid deviation'
      },
      table: {
        header: 'Deviation',
        width: '150px',
        sortable: true,
        displayField: 'deviation',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Timeline Section
    {
      name: 'dueDate',
      label: 'Due Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Due Date', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid due date'
      }
    },
    {
      name: 'resolvedAt',
      label: 'Resolved Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Resolved', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid resolved date'
      }
    },
    {
      name: 'closedAt',
      label: 'Closed Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Closed', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid closed date'
      }
    },
    {
      name: 'closedById',
      label: 'Closed By',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      options: [],
      table: {
        header: 'Closed By',
        width: '150px',
        sortable: true,
        displayField: 'closedBy',
        cellRenderer: 'userDisplay'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
    },
    // File Attachments
    { 
      name: 'fileAttachments', 
      label: 'File(s)', 
      type: 'fileUpload',
      useFormFileUpload2: true, // Use the new responsive FormFileUpload_2 component
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024, // 50MB
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: { 
        header: 'File(s)', 
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount'
      },
      filterable: false,
      validation: {
        required: false
      }
    },
    {
      name: 'referenceDocument',
      label: 'Reference Document',
      type: 'hidden',
      table: {
        header: 'Document Reference',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
        maxLength: 40,
      },
      filterable: true,
      filterType: 'text',
    },
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Basic Information',
      icon: '📄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['code', 'title', 'description', 'complaintTypeId', 'priorityId', 'statusId'],
      fieldSpans: {
        'code': 'md:col-span-4',
        'title': 'md:col-span-8',
        'description': 'md:col-span-12',
        'complaintTypeId': 'md:col-span-4',
        'priorityId': 'md:col-span-4',
        'statusId': 'md:col-span-4',
      },
    },
    {
      title: 'Source / Depositor Information',
      icon: '👤',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['sourceId', 'sourceDetails', 'customerId', 'customerReportedBy', 'sampleAvailableId', 'reportedAt'],
      fieldSpans: {
        'sourceId': 'md:col-span-4',
        'sourceDetails': 'md:col-span-8',
        'customerId': 'md:col-span-4',
        'customerReportedBy': 'md:col-span-4',
        'sampleAvailableId': 'md:col-span-4',
        'reportedAt': 'md:col-span-4',

      }
    },
    {
      title: 'Products',
      icon: '📦',
      layout: 'custom',
      customComponent: ComplaintProductIntegration
    },
    {
      title: 'Complaint Category',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['complaintCategoryNonMedicalId', 'complaintCategoryNonMedicalOther', 'complaintCategoryMedical', 'recordedById', 'recordedAt'],
      fieldSpans: {
        'complaintCategoryNonMedicalId': 'md:col-span-4',
        'complaintCategoryNonMedicalOther': 'md:col-span-8',
        'complaintCategoryMedical': 'md:col-span-12',
        'recordedById': 'md:col-span-4',
        'recordedAt': 'md:col-span-4',
      }
    },
    {
      title: 'Investigation',
      icon: '🔍',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['investigationStartDate', 'investigationCategoryId', 'sampleInspectionId', 'forwardToIds'],
      fieldSpans: {
        'investigationStartDate': 'md:col-span-4',
        'investigationCategoryId': 'md:col-span-8',
        'sampleInspectionId': 'md:col-span-6',
        'forwardToIds': 'md:col-span-6',
      }
    },
    {
      title: 'Actions Taken',
      icon: '✅',
      layout: 'custom',
      customComponent: ComplaintActionTakenIntegration
    },
    {
      title: 'Conclusion',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['findingTypeId', 'involvedDepartmentsIds', 'distributorId'],
      fieldSpans: {
        'findingTypeId': 'md:col-span-4',
        'involvedDepartmentsIds': 'md:col-span-8',
        'distributorId': 'md:col-span-4',
      }
    },
    {
      title: 'Possible Causes',
      icon: '⚠️',
      layout: 'custom',
      customComponent: PossibleCausesAccordion,
      fields: ['humanFactor', 'humanFactorNote', 'equipment', 'equipmentNote', 
               'methods', 'methodsNote', 'materials', 'materialsNote', 
               'supplier', 'supplierNote']
    },    
    {
      title: 'Corrective / Preventive Actions',
      icon: '📋',
      layout: 'custom',
      customComponent: ComplaintActionsIntegration
    },
    {
      title: 'Links',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['riskAssessmentId', 'deviationId'],
      fieldSpans: {
        'riskAssessmentId': 'md:col-span-6',
        'deviationId': 'md:col-span-6'
      }
    },

    {
      title: 'Timeline',
      icon: '📅',
      layout: 'grid',
      gridCols: 'md:grid-cols-3',
      fields: ['dueDate', 'resolvedAt', 'closedAt', 'closedById'],
    },
    {
      title: 'Completion – Observations',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['completionComments',],
      fieldSpans: {
        'completionComments': 'md:col-span-12',
      }
    },

    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    },
    {
      title: 'Reference Document',
      icon: '📄',
      layout: 'custom',
      customComponent: ComplaintReferenceDocument,
      fields: []
    },

  ],

  // Type-based form sections configuration
  // Returns different form sections based on complaint type (GDP vs GMP)
  getFormSectionsByType: function(complaintTypeId, complaintTypes = []) {
    // Get the complaint type name (GDP or GMP)
    const complaintType = complaintTypes.find(t => t.id === complaintTypeId);
    const typeName = complaintType?.name?.toUpperCase() || '';
    
    // Reference Document section - at the end; locked, unlock with password
    const referenceDocumentSection = {
      title: 'Reference Document',
      icon: '📄',
      layout: 'custom',
      customComponent: ComplaintReferenceDocument,
      fields: [] // No fields, custom locked input
    };
    
    // Base sections that apply to all types
    const baseSections = [
      {
        title: 'Basic Information',
        icon: '📄',
        layout: 'grid',
        gridCols: 'md:grid-cols-12',
        fields: ['code', 'title', 'description', 'complaintTypeId', 'priorityId', 'statusId'],
        fieldSpans: {
          'code': 'md:col-span-4',
          'title': 'md:col-span-8',
          'description': 'md:col-span-12',
          'complaintTypeId': 'md:col-span-4',
          'priorityId': 'md:col-span-4',
          'statusId': 'md:col-span-4',
        },
      }
    ];

    // Common sections that can be reused
    const sourceCustomerSection = {
      title: 'Source / Depositor Information',
      icon: '👤',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['sourceId', 'sourceDetails', 'customerId', 'customerReportedBy', 'sampleAvailableId', 'reportedAt'],
      fieldSpans: {
        'sourceId': 'md:col-span-4',
        'sourceDetails': 'md:col-span-8',
        'customerId': 'md:col-span-4',
        'customerReportedBy': 'md:col-span-4',
        'sampleAvailableId': 'md:col-span-4',
        'reportedAt': 'md:col-span-4',
      }
    };

    const productSection = {
      title: 'Products',
      icon: '📦',
      layout: 'custom',
      customComponent: ComplaintProductIntegration
    };

    const categorySection = {
      title: 'Complaint Category',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['complaintCategoryNonMedicalId', 'complaintCategoryNonMedicalOther', 'complaintCategoryMedical', 'recordedById', 'recordedAt'],
      fieldSpans: {
        'complaintCategoryNonMedicalId': 'md:col-span-4',
        'complaintCategoryNonMedicalOther': 'md:col-span-8',
        'complaintCategoryMedical': 'md:col-span-12',
        'recordedById': 'md:col-span-4',
        'recordedAt': 'md:col-span-4',
      }
    };

    // GDP-specific Investigation Section
    const investigationSectionGDP = {
      title: 'Investigation',
      icon: '🔍',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['investigationStartDate', 'investigationCategoryId', 'sampleInspectionId', 'forwardToIds'],
      fieldSpans: {
        'investigationStartDate': 'md:col-span-4',
        'investigationCategoryId': 'md:col-span-8',
        'sampleInspectionId': 'md:col-span-6',
        'forwardToIds': 'md:col-span-6',
      }
    };

    // GMP-specific Investigation Section (with different fields)
    const investigationSectionGMP = {
      title: 'Investigation',
      icon: '🔍',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['investigationStartDate', 'investigationCategoryId', 'involvedDepartmentsIds'], // Example: GMP might not need sampleInspectionId and forwardToIds
      fieldSpans: {
        'investigationStartDate': 'md:col-span-4',
        'investigationCategoryId': 'md:col-span-6',
        'involvedDepartmentsIds': 'md:col-span-6',
      }
    };

    const actionsTakenSection = {
      title: 'Actions Taken',
      icon: '✅',
      layout: 'custom',
      customComponent: ComplaintActionTakenIntegration
    };

    const conclusionSectionGDP = {
      title: 'Conclusion',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['findingTypeId', 'involvedDepartmentsIds', 'distributorId'],
      fieldSpans: {
        'findingTypeId': 'md:col-span-4',
        'involvedDepartmentsIds': 'md:col-span-8',
        'distributorId': 'md:col-span-4',
      }
    };

    const conclusionSectionGMP = {
      title: 'Conclusion',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['findingTypeId', 'distributorId'],
      fieldSpans: {
        'findingTypeId': 'md:col-span-4',
        'distributorId': 'md:col-span-4',
      }
    };

    const possibleCausesSection = {
      title: 'Possible Causes',
      icon: '⚠️',
      layout: 'custom',
      customComponent: PossibleCausesAccordion,
      fields: ['humanFactor', 'humanFactorNote', 'equipment', 'equipmentNote', 
               'methods', 'methodsNote', 'materials', 'materialsNote', 
               'supplier', 'supplierNote']
    };

    const correctiveActionsSection = {
      title: 'Corrective / Preventive Actions',
      icon: '📋',
      layout: 'custom',
      customComponent: ComplaintActionsIntegration
    };

    const linksSection = {
      title: 'Links',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['riskAssessmentId', 'deviationId'],
      fieldSpans: {
        'riskAssessmentId': 'md:col-span-6',
        'deviationId': 'md:col-span-6'
      }
    };

    const timelineSection = {
      title: 'Timeline',
      icon: '📅',
      layout: 'grid',
      gridCols: 'md:grid-cols-3',
      fields: ['dueDate','resolvedAt', 'closedAt', 'closedById'],
    };

    const completionSection = {
      title: 'Completion – Observations',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['completionComments',],
      fieldSpans: {
        'completionComments': 'md:col-span-12',
      }
    };

    const fileAttachmentsSection = {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    };

    // GDP-specific sections (current default order: Source/Customer first, then Product)
    if (typeName === 'GDP') {
      return [
        ...baseSections,
        sourceCustomerSection,
        productSection,
        categorySection,
        investigationSectionGDP,
        //actionsTakenSection,
        conclusionSectionGDP,
        possibleCausesSection,
        correctiveActionsSection,
        linksSection,
        timelineSection,
        completionSection,
        fileAttachmentsSection,
        referenceDocumentSection,
      ];
    }

    // GMP-specific sections (different order: Product first, then Source/Customer)
    if (typeName === 'GMP') {
      return [
        ...baseSections,
        productSection,
        sourceCustomerSection,
        categorySection,
        investigationSectionGMP,
        actionsTakenSection,
        conclusionSectionGMP,
        possibleCausesSection,
        correctiveActionsSection,
        linksSection,
        timelineSection,
        completionSection,
        fileAttachmentsSection,
        referenceDocumentSection,
      ];
    }

    // Default: return original formSections if type not recognized
    return this.formSections;
  },

  // Table Configuration
  tableConfig: {
    defaultSort: { field: 'code', direction: 'desc' },
    defaultPageSize: 25,
    stickyHeader: true,
    rowSelection: true,
    exportOptions: ['csv', 'excel', 'pdf']
  },

  // Data transformation function for preparing data before API submission
  transformData: (data, formMode = 'add') => {
    const transformed = { ...data };

    // Remove fileAttachments from the main complaint data (handled separately)
    delete transformed.fileAttachments;
    
    // Remove complaintActions and complaintActionTaken from the main complaint data (handled separately via APIs)
    delete transformed.complaintActions;
    delete transformed.complaintActionTaken;
    
    // Remove involvedDepartments relation (backend only expects involvedDepartmentsIds)
    delete transformed.involvedDepartments;

    // Strip lookup/display objects that must not be sent to the API
    [
      'complaintType', 'priority', 'source', 'customer', 'distributor',
      'sampleAvailable', 'sampleInspection', 'forwardTo', 'complaintCategoryNonMedical',
      'investigationCategory', 'findingType', 'recordedBy', 'status', 'riskAssessment',
      'deviation', 'closedBy', 'createdBy', 'updatedBy', '_count',
      'fileAttachmentCount',
    ].forEach((key) => {
      delete transformed[key];
    });

    // Audit: createdAt/createdById on create only; updatedAt/updatedById on update only (server / user-id header)
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    if (transformed.referenceDocument !== undefined) {
      if (transformed.referenceDocument === '' || transformed.referenceDocument === null) {
        delete transformed.referenceDocument;
      } else {
        transformed.referenceDocument = String(transformed.referenceDocument).trim();
      }
    }
    
    // Code is now user-provided, so we keep it in the payload
    
    // Convert foreign key fields to integers or send null when empty (so backend can clear values)
    const foreignKeyFields = [
      'complaintTypeId', 'priorityId', 'sourceId', 'customerId', 'distributorId',
      'sampleAvailableId', 'sampleInspectionId', 'complaintCategoryNonMedicalId', 'investigationCategoryId',
      'findingTypeId', 'recordedById', 'riskAssessmentId', 'deviationId', 'closedById'
    ];
    
    foreignKeyFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            transformed[field] = null;
          }
        }
      }
    });
    
    // Handle forwardToIds as array (many-to-many relation); send [] when empty to clear
    if (transformed.forwardToIds !== undefined) {
      if (Array.isArray(transformed.forwardToIds)) {
        const numericArray = transformed.forwardToIds
          .map(id => {
            if (typeof id === 'string') {
              const num = Number(id);
              return isNaN(num) ? null : num;
            }
            if (typeof id === 'number') {
              return isNaN(id) ? null : id;
            }
            return null;
          })
          .filter(id => id !== null);
        transformed.forwardToIds = numericArray;
      } else if (transformed.forwardToIds === '' || transformed.forwardToIds === null) {
        transformed.forwardToIds = [];
      } else {
        const numValue = Number(transformed.forwardToIds);
        if (!isNaN(numValue)) {
          transformed.forwardToIds = [numValue];
        } else {
          transformed.forwardToIds = [];
        }
      }
    }

    // Handle statusId separately since it's required
    if (transformed.statusId !== undefined) {
      if (transformed.statusId === '' || transformed.statusId === null) {
        // Don't delete statusId, set it to a default value or throw error
        throw new Error('Status is required');
      } else {
        const numValue = parseInt(transformed.statusId, 10);
        if (!isNaN(numValue)) {
          transformed.statusId = numValue;
        } else {
          throw new Error('Invalid status ID');
        }
      }
    }

    // Handle involvedDepartmentsIds - ensure it's an array of numbers; send [] when empty to clear
    if (transformed.involvedDepartmentsIds !== undefined) {
      if (Array.isArray(transformed.involvedDepartmentsIds)) {
        const numericArray = transformed.involvedDepartmentsIds
          .map(id => {
            if (typeof id === 'string') {
              const num = Number(id);
              return isNaN(num) ? null : num;
            }
            if (typeof id === 'number') {
              return isNaN(id) ? null : id;
            }
            return null;
          })
          .filter(id => id !== null);
        transformed.involvedDepartmentsIds = numericArray;
      } else if (transformed.involvedDepartmentsIds === '' || transformed.involvedDepartmentsIds === null) {
        transformed.involvedDepartmentsIds = [];
      }
    }
    
    // Convert boolean fields
    const booleanFields = ['humanFactor', 'equipment', 'methods', 'materials', 'supplier'];
    booleanFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = false;
        } else {
          transformed[field] = Boolean(transformed[field]);
        }
      }
    });

    // Clear note text when the related cause switch is off
    const causeNotePairs = [
      ['humanFactor', 'humanFactorNote'],
      ['equipment', 'equipmentNote'],
      ['methods', 'methodsNote'],
      ['materials', 'materialsNote'],
      ['supplier', 'supplierNote'],
    ];
    causeNotePairs.forEach(([switchField, noteField]) => {
      if (!transformed[switchField]) {
        transformed[noteField] = '';
      }
    });

    // Convert date fields to ISO strings or send null when empty (so backend can clear values)
    const dateFields = ['reportedAt', 'recordedAt', 'investigationStartDate',
                        'dueDate', 'resolvedAt', 'closedAt'];
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue.toISOString();
          } else {
            transformed[field] = null;
          }
        }
      }
    });
    
    return transformed;
  },

  // Validation configuration
  validationConfig: {
    enableRealTimeValidation: true,
    validateOnBlur: true,
    validateOnChange: false,
    showValidationSummary: true
  },

  // Notification messages
  messages: {
    loading: 'Loading complaints...',
    noData: 'No complaints found. Create your first complaint to get started.',
    createSuccess: 'Complaint created successfully!',
    updateSuccess: 'Complaint updated successfully!',
    deleteSuccess: 'Complaint deleted successfully!',
    createError: 'Failed to create complaint',
    updateError: 'Failed to update complaint', 
    deleteError: 'Failed to delete complaint',
    loadError: 'Failed to load complaints',
    validationError: 'Validation failed'
  },


  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    if (!this.fields || !Array.isArray(this.fields)) {
      console.warn('⚠️ complaintConfig.fields is not available or not an array');
      return [];
    }
    
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,  // Use displayField as key if available
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        dateFormat: field.table.dateFormat,
        originalField: field.name,  // Keep reference to original field name
        maxLines: field.table.maxLines, // For lineClamp renderer
        maxLength: field.table.maxLength // For truncateWithTooltip renderer
      }));

    // Keep Document Reference as the last visible column
    const referenceIdx = columns.findIndex(
      (col) => col.originalField === 'referenceDocument' || col.key === 'referenceDocument',
    );
    if (referenceIdx >= 0 && referenceIdx !== columns.length - 1) {
      const [referenceCol] = columns.splice(referenceIdx, 1);
      columns.push(referenceCol);
    }
    
    return columns;
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  // Ordered to match formSections structure
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Information',
      icon: '📄',
      fields: [
        'code', 'title', 'complaintTypeId', 'priorityId', 'statusId'
      ]
    },
    {
      name: 'source',
      label: 'Source / Customer',
      icon: '👤',
      fields: [
        'sourceId', 'customerId', 'customerReportedBy', 'sampleAvailableId', 'reportedAt'
      ]
    },
    {
      name: 'products',
      label: 'Products',
      icon: '📦',
      fields: [
        // Products are now handled by ComplaintProductIntegration component
      ]
    },
    {
      name: 'category',
      label: 'Complaint Category',
      icon: '📋',
      fields: [
        'complaintCategoryNonMedicalId', 'recordedById', 'recordedAt'
      ]
    },
    {
      name: 'investigation',
      label: 'Investigation',
      icon: '🔍',
      fields: [
        'investigationStartDate', 'investigationCategoryId', 'sampleInspectionId', 'forwardToIds'
      ]
    },
    {
      name: 'conclusion',
      label: 'Conclusion',
      icon: '✅',
      fields: [
        'findingTypeId', 'involvedDepartmentsIds', 'distributorId'
      ]
    },
    {
      name: 'links',
      label: 'Related Records',
      icon: '🔗',
      fields: [
        'riskAssessmentId', 'deviationId'
      ]
    },
    {
      name: 'timeline',
      label: 'Timeline',
      icon: '📅',
      fields: [
        'dueDate', 'resolvedAt', 'closedAt', 'closedById'
      ]
    }
  ],

  exportFileName: 'qms_complaints',

  // Backend field mappings
  fieldMappings: {
    // Map frontend display fields to backend field paths
    complaintType: 'complaintType.name',
    priority: 'priority.name',
    source: 'source.name',
    customer: 'customer.name',
    distributor: 'distributor.name',
    sampleAvailable: 'sampleAvailable.name',
    sampleInspection: 'sampleInspection.name',
    forwardTo: 'forwardTo.name',
    complaintCategoryNonMedical: 'complaintCategoryNonMedical.name',
    investigationCategory: 'investigationCategory.name',
    findingType: 'findingType.name',
    recordedBy: 'recordedBy.userDisplayName',
    department: 'department.name',
    status: 'status.name',
    riskAssessment: 'riskAssessment.code',
    deviation: 'deviation.code',
    closedBy: 'closedBy.userDisplayName'
  },
  
  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('Complaint'),

  // Labels and Text Configuration (uses predefined Complaint labels + custom overrides)
  labels: {
    // Using predefined Complaint labels from centralized labels file
    // You can override any label by uncommenting and modifying:
    singular: 'Complaint',
    plural: 'Complaints',
    pageTitle: 'Complaint Management',
    pageSubtitle: 'Customer and internal complaint management system',
    searchPlaceholder: 'Search complaints by code or title...',
    viewTitle: 'View Complaint',
    noSelectionText: 'Select a complaint to see details'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Helper methods for approval functionality
  isApprovalEnabled: function() {
    return this.features.approvalRequests && this.approval.enabled;
  },

  shouldShowApprovalInForm: function() {
    return this.isApprovalEnabled() && this.approval.showInForm;
  },

  shouldShowApprovalInDetails: function() {
    return this.isApprovalEnabled() && this.approval.showInDetails;
  },

  requiresApprovalForClose: function() {
    return this.isApprovalEnabled() && this.approval.requireApprovalForClose;
  },

  // Help text for field descriptions
  helpText: {
    code: { short: 'Unique complaint identifier used for tracking and reference.' },
    title: { short: 'Brief, descriptive title of the complaint issue.' },
    description: { short: 'Detailed description of the complaint, including what happened and when.' },
    fileAttachments: { short: 'Supporting documents, evidence, or related files for this complaint.' },
    complaintTypeId: { short: 'Type or category of complaint (GDP, GMP, etc.).' },
    priorityId: { short: 'Priority level based on urgency and business impact.' },
    sourceId: { short: 'Source or channel through which the complaint was received (Pharmacy, Hospital, etc.).' },
    sourceDetails: { short: 'Additional details about the complaint source.' },
    productCode: { short: 'Product code associated with the complaint.' },
    productName: { short: 'Product name associated with the complaint.' },
    batchNumber: { short: 'Batch or lot number of the product in question.' },
    productQuantity: { short: 'Quantity of the product associated with the complaint.' },
    expiryDate: { short: 'Expiry date of the product batch.' },
    customerId: { short: 'Customer or organization that reported the complaint.' },
    distributorId: { short: 'Distributor associated with the complaint.' },
    customerReportedBy: { short: 'Name of the person who reported the complaint on behalf of the customer.' },
    sampleAvailableId: { short: 'Whether a sample of the product is available for investigation (YES, NO, N/A).' },
    sampleInspectionId: { short: 'Type of inspection performed on the complaint sample (ΟΠΤΙΚΟΣ, ΑΛΛΟ).' },
    forwardToIds: { short: 'Departments or entities to forward the complaint to (ΠΡ. ΕΜΠΟΡΙΚΟΥ ΤΜΗΜΑΤΟΣ, ΥΠ. ΑΠΟΘΗΚΗΣ, ΠΕΛΑΤΗΣ, Ν/Α). You can select multiple options.' },
    complaintCategoryNonMedicalId: { short: 'Non-medical category of the complaint (Packaging, Technical, etc.).' },
    complaintCategoryNonMedicalOther: { short: 'Details if "Other" category is selected.' },
    complaintCategoryMedical: { short: 'Medical nature category of the complaint.' },
    recordedById: { short: 'Internal user who recorded this complaint in the system.' },
    recordedAt: { short: 'Date when the complaint was recorded in the system.' },
    investigationStartDate: { short: 'Date when the investigation of this complaint started.' },
    investigationCategoryId: { short: 'Category of investigation conducted.' },
    findingTypeId: { short: 'Type of finding (Confirmed, Unconfirmed, Unjustified).' },
    humanFactor: { short: 'Whether human factor is a possible cause.' },
    humanFactorNote: { short: 'Details about human factor involvement.' },
    equipment: { short: 'Whether equipment is a possible cause.' },
    equipmentNote: { short: 'Details about equipment involvement.' },
    methods: { short: 'Whether methods/processes are a possible cause.' },
    methodsNote: { short: 'Details about methods/processes involvement.' },
    materials: { short: 'Whether materials are a possible cause.' },
    materialsNote: { short: 'Details about materials involvement.' },
    supplier: { short: 'Whether supplier is a possible cause.' },
    supplierNote: { short: 'Details about supplier involvement.' },
    completionComments: { short: 'Final observations and comments upon completion of the complaint investigation.' },

    involvedDepartmentsIds: { short: 'List of departments involved in investigating or handling this complaint. You can select multiple departments.' },
    statusId: { short: 'Current status of the complaint in the resolution workflow.' },
    reportedAt: { short: 'Date when the complaint was first reported or received.' },
    dueDate: { short: 'Deadline for responding to or resolving this complaint.' },
    resolvedAt: { short: 'Date when the complaint was fully resolved.' },
    closedAt: { short: 'Date when the complaint was officially closed.' },
    closedById: { short: 'Person who closed this complaint.' },
    riskAssessmentId: { short: 'Linked risk assessment record if applicable.' },
    deviationId: { short: 'Linked deviation record if complaint is critical.' }
  }
};

export default complaintConfig;
